import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  InviteSuperAdminUserRequest,
  InviteSuperAdminUserResponse,
  inviteSuperAdminUserContract,
  UpdateSuperAdminUserRequest,
  UpdateSuperAdminUserResponse,
  updateSuperAdminUserContract,
  DeleteSuperAdminUserResponse,
  deleteSuperAdminUserContract,
  ResendSuperAdminInvitationResponse,
  resendSuperAdminInvitationContract
} from '@bliks-io/contracts';
import { apiClient } from '@/api/client';
import { superAdminUserKeys } from '@/pages/admin/api/queries/useSuperAdminUsers';

/**
 * Invite SuperAdmin user mutation
 *
 * Creates a new superadmin user and sends an invitation email (super-admin only).
 * - Invalidates superadmin user list cache to refetch updated data
 */
export function useInviteSuperAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      userData: InviteSuperAdminUserRequest
    ): Promise<InviteSuperAdminUserResponse> => {
      const { data } = await apiClient.post<InviteSuperAdminUserResponse>(
        inviteSuperAdminUserContract.path,
        userData
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate superadmin user list to refetch with new user
      queryClient.invalidateQueries({ queryKey: superAdminUserKeys.lists() });
    }
  });
}

/**
 * Update SuperAdmin user mutation
 *
 * Updates an existing superadmin user's details (super-admin only).
 * - Invalidates user list and detail caches to refetch updated data
 */
export function useUpdateSuperAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      updateData: UpdateSuperAdminUserRequest
    ): Promise<UpdateSuperAdminUserResponse> => {
      const { userId, ...body } = updateData;
      const path = updateSuperAdminUserContract.path.replace(':userId', userId);
      const { data } = await apiClient.patch<UpdateSuperAdminUserResponse>(path, body);
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate user list and specific user detail
      queryClient.invalidateQueries({ queryKey: superAdminUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: superAdminUserKeys.detail(variables.userId) });
    }
  });
}

/**
 * Delete SuperAdmin user mutation
 *
 * Soft deletes a superadmin user (super-admin only).
 * - Invalidates user list cache to refetch updated data
 * - Removes user detail from cache
 */
export function useDeleteSuperAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string): Promise<DeleteSuperAdminUserResponse> => {
      const path = deleteSuperAdminUserContract.path.replace(':userId', userId);
      const { data } = await apiClient.delete<DeleteSuperAdminUserResponse>(path);
      return data;
    },
    onSuccess: (_, userId) => {
      // Invalidate user list to refetch without deleted user
      queryClient.invalidateQueries({ queryKey: superAdminUserKeys.lists() });
      // Remove user detail from cache
      queryClient.removeQueries({ queryKey: superAdminUserKeys.detail(userId) });
    }
  });
}

/**
 * Resend SuperAdmin invitation mutation
 *
 * Resends the invitation email to a pending superadmin user (super-admin only).
 * - Invalidates user list cache to refetch updated data
 */
export function useResendSuperAdminInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string): Promise<ResendSuperAdminInvitationResponse> => {
      const path = resendSuperAdminInvitationContract.path.replace(':userId', userId);
      const { data } = await apiClient.post<ResendSuperAdminInvitationResponse>(path, {});
      return data;
    },
    onSuccess: (_, userId) => {
      // Invalidate user list and detail caches
      queryClient.invalidateQueries({ queryKey: superAdminUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: superAdminUserKeys.detail(userId) });
    }
  });
}
